import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { siteWide } from '@helpers/grid';
import { InterfaceIcon } from '@helpers/icons';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import SearchboxStyles from '@stories/Components/Misc/Searchbox/Searchbox.styles';
import styled, { createGlobalStyle } from 'styled-components';
import AnnouncementBarStyles from './AnnouncementBar/AnnouncementBar.styles';
import NavigationStyles from './Navigation/Navigation.styles';
const GlobalStyles = createGlobalStyle `
  :root {
    --headerInnerHeight: ${fluid(68, 80)};
    --announcementHeight: ${fluid(36, 40)};
    --headerHeight: calc(var(--headerInnerHeight) + var(--announcementHeight));
    
    padding-top: var(--headerHeight);

    /* Hide announcement bar on mobile devices if no announcement is configured */
    @media ${until(Device.DesktopSmall)} {
      &:not(:has(${AnnouncementBarStyles.Announcement})) {
        --announcementHeight: 0px
      }
    }
  }
`;
const Container = styled.header `
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: ${Layers.Navigation};
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.08);
  background-color: ${brand.global.white};

  ${ButtonStyles.Container} {
    --buttonHeight: ${fluid(36, 50)};

    white-space: nowrap;
  }

  @media print {
    display: none;
  }
`;
const Header = styled.div `
  ${siteWide};

  --logoWidth: ${fluid(72, 106)};

  display: flex;
  align-items: center;
  gap: 22px;
  height: var(--headerInnerHeight);

  @media ${from(Device.DesktopSmall)} {
    gap: ${fluid(16, 26, Device.Desktop)};
  }
`;
const Logo = styled.a `
  display: block;
  width: var(--logoWidth);
  flex: 0 0 var(--logoWidth);
`;
const CtaWrapper = styled.div `
  display: flex;
  align-items: center;
  gap: ${fluid(8, 20, Device.Desktop)};

  @media ${until(Device.Desktop)} {
    margin-left: auto;

    ${ButtonStyles.Container} {
      --colourText: ${brand.grey.dark};

      height: var(--buttonHeight);
      justify-content: center;
      padding: 0;
      width: var(--buttonHeight);

      &[data-type='primary'] {
        display: none;
      }
    }

    ${ButtonStyles.Container}:not([data-icon-type=${InterfaceIcon.Search}]) {
      --borderColour: ${brand.primary.purple.light};
    }

    ${ButtonStyles.Text} {
      ${srOnly};
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${ButtonStyles.Container}[data-icon-type=${InterfaceIcon.Menu}] {
      display: none;
    }
  }

  @media ${from(Device.Desktop)} {
    flex: 1 1 100%;

    ${ButtonStyles.Container}[data-mobile-only] {
      display: none;
    }
  }

  ${SearchboxStyles.Container} {
    margin-left: auto;
    flex: 1 1 100%;

    @media ${from(Device.DesktopSmall)} {
      max-width: ${fluid(180, 273)};
    }
  }
`;
const SearchboxContainer = styled.div `
  ${transition('max-height, overflow')};

  position: absolute;
  top: 100%;
  width: 100%;
  left: 0;
  height: 74px;
  max-height: 74px;

  ${SearchboxStyles.Container} {
    ${transition('opacity, visibility')};
  }

  &[data-open='false'] {
    max-height: 0;
    overflow: hidden;

    @media ${until(Device.Desktop)} {
      ${SearchboxStyles.Container} {
        visibility: hidden;
        opacity: 0;
      }
    }
  }

  @media ${from(Device.Desktop)} {
    display: contents;
  }
`;
const SearchBoxInner = styled.div `
  background-color: ${brand.global.white};
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 var(--sitePadding);

  @media ${from(Device.Desktop)} {
    display: contents;
  }
`;
const SearchBackground = styled(NavigationStyles.Background) `
  @media ${from(Device.Desktop)} {
    display: none;
  }
`;
const HeaderStyles = {
    Container,
    GlobalStyles,
    Header,
    Logo,
    CtaWrapper,
    SearchboxContainer,
    SearchBoxInner,
    SearchBackground,
};
export default HeaderStyles;
